/**
 * This hook bridges a legacy blocker implementation for Code View's `BlobEditor`
 * Once Code View is fully migrated to DataRouter, this should be removed in favor of useBlocker.
 */

// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
import type {Navigator as BaseNavigator, UNSAFE_createBrowserHistory} from 'react-router'
// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
import {UNSAFE_NavigationContext as NavigationContext} from 'react-router'
import {type ContextType, use, useEffect, useRef} from 'react'

export type BrowserHistory = ReturnType<typeof UNSAFE_createBrowserHistory>

/**
 * A function that receives transitions when navigation is blocked.
 */
interface Blocker {
  (tx: {retry(): void}): void
}

interface GitHubBrowserHistory extends BrowserHistory {
  block(blocker: Blocker): () => void
  dispose: () => void
}

interface Navigator extends BaseNavigator {
  block: GitHubBrowserHistory['block']
}

type NavigationContextWithBlock = ContextType<typeof NavigationContext> & {navigator: Navigator}

/**
 * Original code comment from Sep 27, 2022:
 *
 * // rmorse gist: https://gist.github.com/rmorse/426ffcc579922a82749934826fa9f743
 * // lower level history library example, helpful for seeing use of confirmation flow inside useBlocker:https://github.com/remix-run/history/blob/main/docs/blocking-transitions.md
 *
 * // approach below copied and tweaked from github issue:
 * // https://github.com/remix-run/react-router/issues/8139#issuecomment-1023105785
 *
 * @deprecated - Prefer useBlocker
 */
export function useBlockerLegacy(blocker: Blocker, message: string, when = true) {
  const {navigator} = use(NavigationContext) as NavigationContextWithBlock

  //main tweak required to OP was wrapping unblock in ref so we're only pushing one blocker on the stack for this when expression (i.e. not for every render)
  const unblockRef = useRef<(() => void) | undefined>(undefined)

  // navigator.block uses addEventListener("beforeunload") but this is not checkable by Turbo,
  // so we also set window.onbeforeunload to make Turbo aware of the block
  const previousBeforeUnloadHandlerRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    function cleanup() {
      unblockRef.current?.()
      unblockRef.current = undefined

      window.onbeforeunload = previousBeforeUnloadHandlerRef.current
      previousBeforeUnloadHandlerRef.current = null
    }

    if (!when) {
      cleanup()
      return
    }

    if (!unblockRef.current) {
      // This shouldn't happen:
      if (!navigator.hasOwnProperty('block')) {
        return
      }

      unblockRef.current = navigator.block((tx: {retry(): void}) => {
        const autoUnblockingTx = {
          ...tx,
          retry() {
            unblockRef.current?.() //need to unblock so retry succeeds
            tx.retry()
          },
        }

        blocker(autoUnblockingTx)
      })

      previousBeforeUnloadHandlerRef.current = window.onbeforeunload as () => void
      window.onbeforeunload = () => message
    }

    return cleanup
  }, [navigator, blocker, message, when])
}
